import React, { FC, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useEditorModel } from '../../useEditorModel'
import { useTranslation } from 'react-i18next'
import { IUIBankAccount, UIBankAccountModel, routes } from '@linqpal/models'
import {
  BtAutoComplete,
  BtButton,
  BtEncryptedInput,
  BtLink,
} from '@linqpal/components/src/ui'
import { StyleSheet, View } from 'react-native'
import { BtText } from '@linqpal/components/src/ui'
import { PlaidConnectButton, PlaidConnectButtonRef } from './PlaidConnectButton'
import { toJS } from 'mobx'
import { useResponsive } from '../../../../../utils/hooks'
import { Spacer } from '../../../../../ui/atoms'
import RootStore from '../../../../../store/RootStore'
import { Alert, Description, Question } from '../../components'
import Loading from '../../../../Loading'
import { BankAccountForm } from './BankAccountForm'
import { BankAccountList } from './BankAccountList'
import { CompProps } from '../../Editor'
import { UnsupportedBankAccountModal } from './UnsupportedBankAccountModal'
import {
  CreateBankAccountModel,
  defaultCreateBankAccountModel,
} from './BankAccountFormModel'
import { SkipConnectBankAccountModal } from './SkipConnectBankAccountModal'
import ApplicationStore from '../../ApplicationStore'
import { DeleteBankAccountModal } from './DeleteBankAccountModal'
import { Divider } from '@ui-kitten/components'

interface BankDetailsStepProps extends CompProps {
  setNavigationVisible(visible: boolean): void
}

const defaultBankSearchText = 'AAA'

const BankDetailsStep: FC<BankDetailsStepProps> = ({
  doc,
  item,
  group,
  review,
  onValueUpdate,
  setNavigationVisible,
}) => {
  const { t } = useTranslation('application')
  const { sm } = useResponsive()
  const { bankValidator } = ApplicationStore

  const model = useEditorModel(
    doc,
    group,
    item,
    onValueUpdate,
    null,
    bankValidator,
    UIBankAccountModel,
  )

  const [selectedBank, setSelectedBank] = useState<any>()
  const [manualBankAccount, setManualBankAccount] =
    useState<CreateBankAccountModel>(defaultCreateBankAccountModel)

  const [warningMessage, setWarningMessage] = useState<string>()
  const [searchValue, setSearchValue] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isManualFormVisible, setIsManualFormVisible] = useState(false)
  const [isSkipConnectModalVisible, setIsSkipConnectModalVisible] =
    useState(false)
  const [bankAccountToDelete, setBankAccountToDelete] = useState<string>()
  const plaidConnectButtonRef = useRef<PlaidConnectButtonRef>(null)

  const isLoanApplication = doc.type !== 'supplier_application'

  const {
    userStore,
    screensStore: { paymentMethodsStore },
    isBusy,
  } = RootStore

  const bankAccounts = paymentMethodsStore.paymentMethods.filter(
    (pm) => pm.paymentMethodType === 'bank',
  )

  const updateBankAccountModel = (bankAccount: IUIBankAccount | null) => {
    model.setValue(
      'account',
      bankAccount ? UIBankAccountModel.create(toJS(bankAccount)) : null,
    )
  }

  const searchBanks = (
    params: Parameters<typeof routes.company.allInstitutions>[0],
  ) => {
    if (params.search.length >= defaultBankSearchText.length) {
      return routes.plaid
        .searchInstitution(params.search)
        .then((response) => response.items)
    } else {
      return Promise.resolve([])
    }
  }

  const getPlaidBanks = async () => {
    const getBanks = () => {
      return paymentMethodsStore.paymentMethods?.filter(
        (bank) => !bank.isManualEntry,
      )
    }
    const connectedBanks = getBanks()
    await paymentMethodsStore.fetchPaymentMethods()
    const plaidBanks = getBanks()

    const addedBank = plaidBanks.find(
      (fb) => !connectedBanks.some((cb) => cb._id === fb._id),
    )
    if (!addedBank) {
      setWarningMessage(t('Bank.UnsupportedAccountsWarning'))
      return
    }

    updateBankAccountModel(addedBank)
    ApplicationStore.setIsPrimaryBankAccountChanged(true)
  }

  const handleSearchValueChange = (newValue: string) => {
    setSearchValue(newValue)
    if (!newValue) {
      setSelectedBank(undefined)
    }
  }

  const handleSelectBankAccount = (bankAccount: IUIBankAccount) => {
    updateBankAccountModel(bankAccount)
    ApplicationStore.setIsPrimaryBankAccountChanged(true)
  }

  const handleSelectBank = (bank) => {
    setSelectedBank(bank)
    setSearchValue(bank.name)
  }

  const handleSuccessfulBankAccountConnect = async () => {
    setIsLoading(true)
    try {
      await userStore.fetchUser()
      await getPlaidBanks()

      setSelectedBank(undefined)
      setSearchValue('')
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseWarningModal = () => {
    setWarningMessage(undefined)
  }

  const handleCancelAddBankAccount = () => {
    setNavigationVisible(true)
    setIsManualFormVisible(false)
    setManualBankAccount(defaultCreateBankAccountModel)
  }

  const handleAddManualBankAccount = async () => {
    setNavigationVisible(true)
    setIsManualFormVisible(false)

    try {
      const primary = ApplicationStore.isCreditApplication
        ? { isPrimaryForCredit: true }
        : { isPrimary: true }
      const response = await routes.company.addBankAccount({
        ...manualBankAccount,
        ...primary,
        isManualEntry: true,
      })

      await paymentMethodsStore.fetchPaymentMethods()

      const newManualBankAccount = paymentMethodsStore.manuallyAddedBanks.find(
        (b) => b.id === response.id,
      )!
      updateBankAccountModel(toJS(newManualBankAccount))
      setManualBankAccount({
        ...defaultCreateBankAccountModel,
        name: searchValue,
      })

      setSelectedBank(undefined)
      setSearchValue('')
    } catch (error) {
      console.error(error)
    }
  }

  const handleClickEnterManually = () => {
    if (!isSelectedBankSupportedByPlaid) {
      handleSkipConnectAccepted()
      return
    }

    setIsSkipConnectModalVisible(true)
  }

  const handleCloseSkipConnect = () => {
    setIsSkipConnectModalVisible(false)
  }

  const handleSkipConnectAccepted = () => {
    setNavigationVisible(false)
    setIsManualFormVisible(true)
    setIsSkipConnectModalVisible(false)
    setManualBankAccount((prev) => ({ ...prev, name: searchValue }))
  }

  const handleLoginToBank = () => {
    setIsSkipConnectModalVisible(false)
    plaidConnectButtonRef.current?.createToken()
  }

  const handleManualBankAccountChange = (newValue: CreateBankAccountModel) => {
    setManualBankAccount(newValue)
  }

  const handleClickDeleteManualBankAccount = (id: string) => {
    setBankAccountToDelete(id)
  }

  const handleCloseDeleteManualBankAccountModal = () => {
    setBankAccountToDelete(undefined)
  }

  const handleDeleteManualBankAccount = async () => {
    if (!bankAccountToDelete) return

    try {
      setIsDeleting(true)

      await routes.company.removeBankAccount(bankAccountToDelete)

      await paymentMethodsStore.fetchPaymentMethods()

      updateBankAccountModel(null)
    } catch (error) {
      console.error(error)
    } finally {
      setIsDeleting(false)
      setBankAccountToDelete(undefined)
    }
  }

  const getTitle = () => {
    if (hasAccountAdded) {
      return 'Bank.TitleConnected'
    } else if (selectedBank) {
      return 'Bank.TitleSelected'
    } else {
      return isLoanApplication ? 'Bank.TitleLoan' : 'Bank.Title'
    }
  }

  const getDescription = () => {
    if (hasAccountAdded) {
      return 'Bank.DescriptionConnected'
    } else if (isLoanApplication) {
      return selectedBank
        ? 'Bank.DescriptionLoanSelected'
        : 'Bank.DescriptionLoan'
    } else {
      return selectedBank ? 'Bank.DescriptionSelected' : 'Bank.Description'
    }
  }

  const isManualBankAccountValid = useMemo(
    () => UIBankAccountModel.create(toJS(manualBankAccount)).isFilled,
    [manualBankAccount],
  )

  const isSelectedBankSupportedByPlaid = selectedBank?.name === searchValue

  const hasAccountAdded = bankAccounts.length > 0

  if (isLoading) {
    return (
      <View style={{ height: 100 }}>
        <Loading />
      </View>
    )
  }

  if (!review) {
    if (!sm && isManualFormVisible) {
      return (
        <>
          <BankAccountForm
            review={review}
            model={manualBankAccount}
            onChange={handleManualBankAccountChange}
          />
          <Spacer height={16} />
          <BtButton
            testID="BankAccountFormAdd"
            disabled={!isManualBankAccountValid}
            onPress={handleAddManualBankAccount}
          >
            {t('Bank.AcceptManual')}
          </BtButton>
          <Spacer height={16} />
          <BtButton
            testID="BankAccountFormCancel"
            status={'basic'}
            onPress={handleCancelAddBankAccount}
          >
            {t('Bank.CancelManual')}
          </BtButton>
        </>
      )
    } else {
      return (
        <View>
          <View>
            <Question t={t} sm={sm} title={getTitle()} />
            <Description description={getDescription()} sm={sm} t={t} />
            {hasAccountAdded && (
              <BtText
                style={{
                  color: '#335C75',
                  fontSize: sm ? 18 : 16,
                  fontWeight: '600',
                }}
              >
                {isLoanApplication
                  ? t('Bank.BankListLoanSelectTitle')
                  : t('Bank.BankListGetPaidSelectTitle')}
              </BtText>
            )}
            {hasAccountAdded && (
              <BtText
                style={{
                  color: '#335C75',
                  fontSize: sm ? 16 : 14,
                  marginTop: 10,
                  marginBottom: 30,
                }}
              >
                {isLoanApplication
                  ? t('Bank.BankListLoanSelectDescription')
                  : t('Bank.BankListGetPaidSelectDescription')}
              </BtText>
            )}
          </View>

          <BankAccountList
            bankAccounts={bankAccounts}
            selectedBankAccountId={model?.value?._id}
            onSelect={handleSelectBankAccount}
            onDelete={handleClickDeleteManualBankAccount}
          />

          {hasAccountAdded && (
            <>
              <Divider
                style={{
                  backgroundColor: '#CCD6DD',
                  marginTop: 10,
                  marginBottom: 20,
                }}
              />
              <BtText
                style={{
                  color: '#335C75',
                  fontSize: sm ? 24 : 16,
                  fontWeight: '600',
                  marginBottom: 18,
                }}
              >
                {t('Bank.SearchAnotherBankTitle')}
              </BtText>
            </>
          )}

          <BtEncryptedInput
            size="large"
            disabled={isBusy}
            value={searchValue}
            label={t('Bank.SearchBankLabel')}
            testID="bank_details_search"
            InputComponent={BtAutoComplete}
            labelSelector={(option) => option.name}
            placeholder={t('Bank.DetailsPlaceHolder')}
            searchParams={{ limit: 25, onlyFinciity: true }}
            //eslint-disable-next-line i18next/no-literal-string
            placement="bottom end"
            apiSearchFn={searchBanks}
            onSelect={handleSelectBank}
            onChangeText={handleSearchValueChange}
            defaultSearchText={defaultBankSearchText}
          />

          <Spacer height={24} />

          {!!searchValue && (
            <View
              style={{
                alignItems: 'center',
              }}
            >
              {isSelectedBankSupportedByPlaid && (
                <>
                  <PlaidConnectButton
                    style={{ marginTop: 20 }}
                    ref={plaidConnectButtonRef}
                    disabled={isBusy}
                    text={t('Bank.LoginToBankAccount')}
                    onConnectSuccess={handleSuccessfulBankAccountConnect}
                    highlightRountingNumber={selectedBank.routing_numbers[0]}
                  />
                  <Spacer height={10} />
                </>
              )}
              <BtLink
                disabled={isBusy}
                title={t('Bank.EnterBankDetailsManually')}
                testID="EnterBankDetailsManually"
                onPress={handleClickEnterManually}
                textStyle={
                  isBusy
                    ? [styles.linkText, styles.disabledLinkText]
                    : styles.linkText
                }
              >
                {t('Bank.EnterBankDetailsManually')}
              </BtLink>
            </View>
          )}

          <Alert
            title={t('Bank.EnterBankDetailsManually')}
            visible={isManualFormVisible}
            buttons={[
              {
                testID: 'BankAccountFormCancel',
                label: t('Bank.CancelManual'),
                onPress: handleCancelAddBankAccount,
              },
              {
                testID: 'BankAccountFormAdd',
                label: t('Bank.AcceptManual'),
                disabled: !isManualBankAccountValid,
                onPress: handleAddManualBankAccount,
              },
            ]}
            onClose={handleCancelAddBankAccount}
          >
            <BankAccountForm
              review={review}
              model={manualBankAccount}
              onChange={handleManualBankAccountChange}
            />
          </Alert>

          <SkipConnectBankAccountModal
            isVisible={isSkipConnectModalVisible}
            isLoanApplication={isLoanApplication}
            onLoginToBank={handleLoginToBank}
            onEnterManually={handleSkipConnectAccepted}
            onClose={handleCloseSkipConnect}
          />

          <UnsupportedBankAccountModal
            message={warningMessage}
            isVisible={!!warningMessage}
            onClose={handleCloseWarningModal}
          />

          <DeleteBankAccountModal
            isVisible={!!bankAccountToDelete}
            isLoading={isDeleting}
            onConfirm={handleDeleteManualBankAccount}
            onClose={handleCloseDeleteManualBankAccountModal}
          />
        </View>
      )
    }
  } else {
    return (
      <>
        <Spacer height={24} />
        <BankAccountList
          bankAccounts={bankAccounts}
          selectedBankAccountId={model.value._id}
          onSelect={handleSelectBankAccount}
        />
      </>
    )
  }
}

export default {
  component: observer(BankDetailsStep),
}

const styles = StyleSheet.create({
  linkText: {
    marginTop: 10,
    marginBottom: 20,
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 24,
  },
  disabledLinkText: {
    color: '#99ADBA',
  },
})
